import * as Sentry from '@sentry/react';
import { useCheckTrialEligibilityQuery } from '_services/Api/Membership/MembershipApi';
import { UserShape } from '_types/User';
import { VideoTutorialCategoryShape } from '_types/VideoTutorial';
import moment from 'moment';
import useUser from './useUser';

export const isStage = (): boolean => {
    const domain = window.location.hostname;
    return !!(
        domain.includes('kan.dev') ||
        domain.includes('stage.') ||
        process.env.HOST?.includes('stage')
    );
};

export const isDev = (): boolean => {
    const domain = window.location.hostname;
    return !!domain.includes('kan.dev');
};

export const currentUserNeedsReboarding = (
    user: Pick<UserShape, 'created_at'> | undefined,
): boolean => {
    //User might not be loaded yet
    if (!user) {
        return false;
    }
    //User is loaded but something is invalid
    if (!user?.created_at || !moment(user?.created_at).isValid()) {
        Sentry.captureException(
            new Error('Invalid user created_at ' + user?.created_at),
            {
                extra: { user },
            },
        );
        return false;
    }

    const cutoffDate = moment('2025-07-03');
    const userCreatedAt = moment(user.created_at);

    return userCreatedAt.isBefore(cutoffDate);
};

const getCategoryIsPremium = (
    category: VideoTutorialCategoryShape,
    isPremiumContentGated: boolean,
): boolean => {
    if (!isPremiumContentGated) return false;
    // Lær å strikke + Basic teknikker are NOT gated. Categories refers to category id, will
    // vary between stage and prod.
    let notGatedCategories = [1, 2];

    if (isStage()) {
        notGatedCategories = [2, 16];
    }
    return !notGatedCategories.includes(category.id);
};

const getIsPremiumContentGated = (
    user: Pick<UserShape, 'membership_summary' | 'created_at'> | undefined,
): boolean => {
    if (!user) return true;

    if (isStage()) {
        // return true;
    }

    const isPremium = user?.membership_summary?.type !== 'free';

    // If user is premium, content is not gated
    return !isPremium;
};

const getIsInFebruary2026Campaign = (user: UserShape | undefined): boolean => {
    // KN-487 Discount message to free users
    // Not given to users created after 15 feb 2026
    if (user && moment(user.created_at).isAfter('2026-02-15')) return false;
    // Offer ends afte April 2026
    if (moment().isAfter('2026-04-30')) return false;
    return true;
};

export const JUNE_2026_SUMMER_CAMPAIGN_START = '2026-06-24T00:00:00+02:00';
export const JUNE_2026_SUMMER_CAMPAIGN_END = '2026-06-30T23:59:59+02:00';

const getIsInJune2026SummerCampaign = (): boolean => {
    const now = moment();
    if (isStage()) {
        console.warn(
            'getIsInJune2026SummerCampaign always returns true on stage, but it does not reflect prod conditions',
        );
        return true;
    }
    return (
        now.isSameOrAfter(JUNE_2026_SUMMER_CAMPAIGN_START) &&
        now.isSameOrBefore(JUNE_2026_SUMMER_CAMPAIGN_END)
    );
};

const usePremiumInfo = (): {
    getCategoryIsPremium: (category: VideoTutorialCategoryShape) => boolean;
    isPremiumContentGated: boolean;
    isEligibleForFreeTrial: boolean;
    isLoadingTrialEligibility: boolean;
    isPremium: boolean;
    isActiveTrial: boolean;
    trialEndsAt: string | undefined;
    isStage: boolean;
    isInFebruary2026Campaign: boolean;
    isInJune2026SummerCampaign: boolean;
} => {
    const { user } = useUser();
    const skipTrialEligibility = !user?.id; // avoid unauthenticated calls
    const { data: trialEligibilityData, isLoading: isLoadingTrialEligibility } =
        useCheckTrialEligibilityQuery(undefined, {
            skip: skipTrialEligibility,
        });

    const isPremiumContentGated = getIsPremiumContentGated(user);
    const isEligibleForFreeTrial =
        trialEligibilityData?.eligible_for_trial ?? false;
    return {
        getCategoryIsPremium: (category: VideoTutorialCategoryShape) =>
            getCategoryIsPremium(category, isPremiumContentGated),
        isPremiumContentGated,
        isEligibleForFreeTrial,
        isLoadingTrialEligibility,
        isPremium: !isPremiumContentGated,
        isActiveTrial: !!trialEligibilityData?.trial_active,
        trialEndsAt: trialEligibilityData?.trial_ends_at ?? undefined,
        isStage: isStage(),
        isInFebruary2026Campaign: getIsInFebruary2026Campaign(user),
        isInJune2026SummerCampaign: getIsInJune2026SummerCampaign(),
    };
};

export const usePremiumInfo_test = (): {
    getIsPremiumContentGated: (
        user: Pick<UserShape, 'membership_summary' | 'created_at'> | undefined,
    ) => boolean;
    getCategoryIsPremium: (
        category: VideoTutorialCategoryShape,
        isPremiumContentGated: boolean,
    ) => boolean;
} => {
    return {
        getIsPremiumContentGated: getIsPremiumContentGated,
        getCategoryIsPremium: getCategoryIsPremium,
    };
};

export default usePremiumInfo;
